import keyMirror from 'keymirror';

/**
 * Some common syntax for actions...
 *
 * Standard format is:
 *
 * {CATEGORY}__{ACTION}
 *
 * ...where both CATEGORY and ACTION are intended to be meaningful to the end user, as these are used directly used in audit data UI
 * with underscores replaced by spaces and cast to lowercase.
 *
 * CATEGORY is intended to be a top-level filtering criteria to help end user quickly filter into area of audit data they are
 * interested in.
 *
 * ACTION is also intended to be meaningful to the end user and generally follow the following rules:
 *
 *  - ACTION should be in past tense, as this is used to represent an event that has taken place in the application.
 *  - data/service read events should use *_LOADED.
 *  - data/service creation events should use *_CREATED.
 *  - data/service update event should use *_UPDATED.
 *  - (not currently used) data/service delete events should use *_DELETED, even if data store record is only being updated to be marked
 *  as deleted/inactive.  To the end user, this is a deletion. RUle of thumb is that if the deletion means the end user will never be
 *  be able to see the data again, the action should be *_DELETED.
 *  - UI events the might change application state should use *_CHANGED (for multivariate events) or *_TOGGLED (for binary state events).
 *  - *_STARTED can be used when action represents the beginning of some sequence of actions.
 *  - *_COMPLETED can be used when action represents to completion of some sequence of actions. This also implies "successful" completion.
 *  - *_FAILED should only be used for cases where an Error object is being passed in the action payload.
 *  - action syntax should follow object -> verb ordering.  For example, USER_CREATED should be used instead of CREATED_USER.
 */

const Actions = keyMirror({
  APPLICATION__ERROR: null,

  AUDIT__CONTEXT_PASSED: null,

  AUDIT_DASHBOARD__ACTION_QUERY_COMPLETED: null,
  AUDIT_DASHBOARD__ACTION_QUERY_FAILED: null,
  AUDIT_DASHBOARD__ACTION_QUERY_STARTED: null,
  AUDIT_DASHBOARD__CSV_DOWNLOAD_QUERY_COMPLETED: null,
  AUDIT_DASHBOARD__CSV_DOWNLOAD_QUERY_FAILED: null,
  AUDIT_DASHBOARD__CSV_DOWNLOAD_QUERY_STARTED: null,
  AUDIT_DASHBOARD__DATA_QUERY_COMPLETED: null,
  AUDIT_DASHBOARD__DATA_QUERY_FAILED: null,
  AUDIT_DASHBOARD__DATA_QUERY_STARTED: null,

  AUTHENTICATION__ACTIVATION_KEY_FOUND: null,
  AUTHENTICATION__AUTHORIZATION_COMPLETED: null,
  AUTHENTICATION__AUTHORIZATION_STARTED: null,
  AUTHENTICATION__CHECK_COMPLETED: null,
  AUTHENTICATION__CHECK_STARTED: null,
  AUTHENTICATION__COMPLETED: null,
  AUTHENTICATION__FAILED: null,
  AUTHENTICATION__FORM_STATE_CLEARED: null,
  AUTHENTICATION__LOGOUT_COMPLETED: null,
  AUTHENTICATION__LOGOUT_STARTED: null,
  AUTHENTICATION__REJECTED: null,
  AUTHENTICATION__STARTED: null,
  AUTHENTICATION__PASSWORD_FORGOT_COMPLETED: null,
  AUTHENTICATION__PASSWORD_FORGOT_FAILED: null,
  AUTHENTICATION__PASSWORD_FORGOT_STARTED: null,
  AUTHENTICATION__PASSWORD_RESET_COMPLETED: null,
  AUTHENTICATION__PASSWORD_RESET_FAILED: null,
  AUTHENTICATION__PASSWORD_RESET_FORM_STATE_CHANGED: null,
  AUTHENTICATION__PASSWORD_RESET_STARTED: null,

  BILLING__COST_TIER_LOADED: null,
  BILLING__GUIDE_COUNT_UPDATED: null,
  BILLING__PAYMENT_TERM_UPDATED: null,
  BILLING__REQUEST_CANCELLED: null,
  BILLING__REQUEST_STARTED: null,
  BILLING__SUBMIT_REQUEST_COMPLETED: null,
  BILLING__SUBMIT_REQUEST_FAILED: null,
  BILLING__SUBMIT_REQUEST_STARTED: null,
  BILLING__SUBSCRIPTION_LOADED: null,
  BILLING__SUPPORT_PACKAGE_UPDATED: null,
  BILLING__TRANSACTION_HISTORY_LOADED: null,

  CLIENT_ACCOUNT__ACCOUNT_CREATED: null,
  CLIENT_ACCOUNT__ACCOUNT_CREATION_STARTED: null,
  CLIENT_ACCOUNT__DOMAIN_VALIDATION_COMPLETED: null,
  CLIENT_ACCOUNT__LOADED: null,
  CLIENT_ACCOUNT__ORG_UNIT_CREATED: null,
  CLIENT_ACCOUNT__ORG_UNIT_SEARCH_TERM_CHANGED: null,
  CLIENT_ACCOUNT__ORG_UNIT_UPDATED: null,
  CLIENT_ACCOUNT__SHOW_INACTIVE_ORG_UNITS_TOGGLED: null,
  CLIENT_ACCOUNT__SIGNUP_FORM_RESET: null,
  CLIENT_ACCOUNT__ACCOUNT_UPDATED: null,

  COMPLIANCE__CONVERSATION_QUERY_STARTED: null,
  COMPLIANCE__CONVERSATION_QUERY_COMPLETED: null,
  COMPLIANCE__CONVERSATION_QUERY_CLEARED: null,
  COMPLIANCE__CONVERSATION_QUERY_FAILED: null,
  COMPLIANCE__LIST_ITEM_FOCUSED: null,
  COMPLIANCE__QUERY_COMPLETED: null,
  COMPLIANCE__QUERY_FAILED: null,
  COMPLIANCE__QUERY_STARTED: null,

  CONFIG__BUILD_DATA_RECEIVED: null,
  CONFIG__LOADED: null,

  CONVERSATION_HISTORY__LOADED: null,
  CONVERSATION_HISTORY__CATEGORY_CHANGED: null,

  EMPLOYEES__LOAD_ALL_COMPLETED: null,
  EMPLOYEES__LOAD_ALL_STARTED: null,

  GUIDES__ALL_GUIDES_LOADED: null,
  GUIDES__DOMAIN_VALIDATION_COMPLETED: null,
  GUIDES__GUIDE_LOADED: null,
  // GUIDES__GUIDE_SAVE_COMPLETED: null,
  // GUIDES__GUIDE_SAVE_STARTED: null,

  HELP_VIDEO__QUERY_COMPLETED: null,
  HELP_VIDEO__QUERY_FAILED: null,
  HELP_VIDEO__QUERY_STARTED: null,

  HISTOGRAM__DATA_LOADED: null,

  INTL__MESSAGES_LOADED: null,

  NAVIGATION__STARTED: null,
  NAVIGATION__COMPLETED: null,
  NAVIGATION__FAILED: null,

  ONLINE_LEADER__DATA_LOAD_STARTED: null,
  ONLINE_LEADER__DATA_LOADED: null,
  ONLINE_LEADER__QUERY_CHANGED: null,
  // ONLINE_LEADER__REGIONS_CHANGED: null,

  ONLINE_PERFORMANCE__DATA_LOAD_STARTED: null,
  ONLINE_PERFORMANCE__DATA_LOADED: null,
  ONLINE_PERFORMANCE__QUERY_CHANGED: null,
  ONLINE_PERFORMANCE__INTERVAL_CHANGED: null,
  // ONLINE_PERFORMANCE__REGIONS_CHANGED: null,

  PRODUCTS__ASSOCIATED_PRODUCTS_LOADED: null,
  PRODUCTS__EXCLUDED_PRODUCTS_QUERY_COMPLETED: null,
  PRODUCTS__EXCLUDED_PRODUCTS_QUERY_FAILED: null,
  PRODUCTS__EXCLUDED_PRODUCTS_QUERY_STARTED: null,
  PRODUCTS__EXCLUDED_PRODUCTS_TOGGLED: null,
  PRODUCTS__LOCATION_ALL_CLIENT_ORG_UNITS_LOADED: null,
  PRODUCTS__LOCATION_SEARCH_TERM_CHANGED: null,
  PRODUCTS__PRODUCT_COST_TYPES_LOADED: null,
  PRODUCTS__PRODUCT_EDIT_STARTED: null,
  PRODUCTS__PRODUCT_EDIT_COMPLETED: null,
  PRODUCTS__REQUIRED_PRODUCTS_LOADED: null,
  PRODUCTS__PRODUCT_RULE_TYPES_LOADED: null,
  PRODUCTS__PRODUCT_UPDATE_STARTED: null,
  PRODUCTS__PRODUCTS_LOADED: null,

  PRODUCT_ACTION__DATA_LOAD_STARTED: null,
  PRODUCT_ACTION__DATA_LOADED: null,
  PRODUCT_ACTION__QUERY_CHANGED: null,

  PRODUCT_MIX__DATA_LOAD_STARTED: null,
  PRODUCT_MIX__DATA_LOADED: null,
  PRODUCT_MIX__QUERY_CHANGED: null,
  // PRODUCT_MIX__REGIONS_CHANGED: null,

  PUBLISHING__PUBLICATION_LOADED: null,
  PUBLISHING__PUBLICATION_UPDATED: null,

  SALES_LEADER__DATA_LOAD_STARTED: null,
  SALES_LEADER__DATA_LOADED: null,
  SALES_LEADER__QUERY_CHANGED: null,

  TEAM_PERFORMANCE__DATA_LOAD_STARTED: null,
  TEAM_PERFORMANCE__DATA_LOADED: null,
  TEAM_PERFORMANCE__QUERY_CHANGED: null,
  // TEAM_PERFORMANCE__REGIONS_CHANGED: null,

  TRAFFIC_SUMMARY__DATA_LOAD_STARTED: null,
  TRAFFIC_SUMMARY__DATA_LOADED: null,
  TRAFFIC_SUMMARY__DEVICE_HISTORY_DATA_LOAD_STARTED: null,
  TRAFFIC_SUMMARY__QUERY_CHANGED: null,
  // TRAFFIC_SUMMARY__REGIONS_CHANGED: null,
  TRAFFIC_SUMMARY__USER_ACTION_DATA_LOAD_STARTED: null,

  UI__FAVORITE_CREATED: null,
  UI__FAVORITES_DELETED: null,
  UI__FAVORITES_LOADED: null,
  UI__LIST_FILTER_CLEARED: null,
  UI__LIST_FILTER_SET: null,
  UI__SIDEBAR_TOGGLED: null,

  USER_ACCOUNT__CLIENT_CHANGED: null,
  USER_ACCOUNT__EDIT_CANCELLED: null,
  USER_ACCOUNT__EDIT_COMPLETED: null,
  USER_ACCOUNT__EDIT_STARTED: null,
  USER_ACCOUNT__MESSAGES_CLEARED: null,

  USER_MANAGEMENT__LIST_MANAGED_USERS: null,
  USER_MANAGEMENT__LIST_USER_ROLES: null,
  USER_MANAGEMENT__ORG_UNIT_HIERARCHY_AND_TERMS_LOADED: null,
  USER_MANAGEMENT__PASSWORD_RESET_CONFIRMATION_DISPLAYED: null,
  USER_MANAGEMENT__PASSWORD_RESET_EMAIL_SENT: null,
  USER_MANAGEMENT__ROLES_DISABLED: null,
  USER_MANAGEMENT__ROLES_ENABLED: null,
  USER_MANAGEMENT__SEARCH_TERM_CHANGED: null,
  USER_MANAGEMENT__SHOW_INACTIVE_USERS_TOGGLED: null,
  USER_MANAGEMENT__USER_CREATED: null,
  USER_MANAGEMENT__USER_CREATED_REMINDER_CLEARED: null,
  USER_MANAGEMENT__USER_CREATED_REMINDER_DISPLAYED: null,
  USER_MANAGEMENT__USER_HAD_ROLE_STATUS_TOGGLED: null,
  USER_MANAGEMENT__USER_HAD_ROLES_UPDATED: null,
  USER_MANAGEMENT__USER_PII_UPDATED: null,
  USER_MANAGEMENT__VALIDATION_ERRORS_CLEARED: null,
  USER_MANAGEMENT__VALIDATION_ERRORS_RECEIVED: null
});

/**
 * This schema version must be incremented if there are any breaking changes in action definitions.
 * A breaking change would occur if any of the category or action parts of existing actions are changed.
 * Adding new categories/actions would not constitute a breaking change.
 *
 * @type {number}
 */
const schemaVersion = 1;

export {
  Actions as default,
  schemaVersion
};



// WEBPACK FOOTER //
// ./src/constants/Actions.js